/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React from 'react';
import styles from './app.module.css';
import { Switch, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'recompose';
import { Loader, Sidebar } from 'semantic-ui-react';
import * as routes from '../../constants/routes';
import withAuthentication from '../../components/Session/withAuthentication';
import injectSaga from '../../utils/injectSaga';
import injectReducer from '../../utils/injectReducer';
import appSelectors from './selectors';
import reducer from './reducer';
import saga from './saga';
import ErrorBoundary from '../../components/ErrorBoundary';
import { isMobileOnly } from 'react-device-detect';
import AppNavigator from '../../components/AppNavigator';
import actions from './actions';

import { FlexBox } from '../../components/common';

import WorkflowManager from '../../containers/WorkflowManager/Loadable';
import Inventory from '../../containers/Inventory/Loadable';
import Dashboard from '../../containers/Dashboard/Loadable';
import ConfigurationManager from '../../containers/ConfigurationManager/Loadable';
import WebhooksPage from '../../containers/Webhooks/Loadable';
import NotFoundPage from '../../containers/NotFoundPage/Loadable';
import SignInPage from '../../components/SignIn';
import NoAccessPage from '../../containers/NoAccess/Loadable';
import AccountPage from '../../containers/Account/Loadable';
import DragDropOverlay from '../../components/common/DragDropOverlay';

import meta from './meta.json';
import ThemeController from '../../components/common/theme/ThemeController';
import { DOCKET_SHOP_MISMATCH } from './docketShopValidation';
export class App extends React.PureComponent {
  constructor(props) {
    super(props);
    let showSidebar = true;
    if (this.props.app.authUser === null) {
      showSidebar = false;
    }

    this.state = {
      showSidebar: showSidebar,
      showMobileSidebar: false,
    };

    window.themeController = new ThemeController();
    window.themeController.update();

    console.log(`[survcart/app] ${meta.version}`);

    this.layoutCount = 0;
    this.elementToCallbackMap = {};
  }

  easeToValue = (currentValue, finalValue, ease) => {
    if (Math.abs(currentValue - finalValue) < 0.001) {
      return finalValue;
    } else {
      return currentValue + (finalValue - currentValue) * ease;
    }
  };

  componentDidMount() {
    document.getElementsByTagName('body')[0].style.overflowX = 'hidden';
  }

  sidebarClosed() {
    if (!this.state.showSidebar) {
      this.setState({ showSidebar: true });
    }
  }

  showMobileSidebar() {
    if (!this.state.showMobileSidebar) {
      this.setState({ showMobileSidebar: true });
    }
  }

  closeMobileSidebar() {
    if (
      isMobileOnly &&
      this.state.showMobileSidebar &&
      this.state.showMobileSidebar
    ) {
      this.setState({ showMobileSidebar: false });
    }
  }

  switchDevMode() {
    if (localStorage) {
      let co = localStorage.getItem('co');
      if (co === 'd') {
        localStorage.setItem('co', 'p');
      } else {
        localStorage.setItem('co', 'd');
      }
      window.location.reload();
    }
  }

  render() {
    if (this.props.app.docketShopValidationError) {
      const message =
        this.props.app.docketShopValidationError === DOCKET_SHOP_MISMATCH
          ? "This storefront is linked to a different company's account and has been disabled to prevent editing the wrong storefront. Contact support."
          : 'Unable to validate this storefront right now. Contact support.';

      return (
        <div
          role="alert"
          style={{
            alignItems: 'center',
            display: 'flex',
            fontFamily: 'Roboto',
            fontSize: '1.25em',
            height: '100%',
            justifyContent: 'center',
            padding: '2em',
            textAlign: 'center',
          }}
        >
          {message}
        </div>
      );
    }

    let content = undefined;
    let loader = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: window.innerHeight * 0.75,
        }}
      >
        <Loader size="massive" active inline="centered">
          <span
            style={{ fontFamily: 'Roboto', color: '#444', fontWeight: '300' }}
          >
            <span className="loaderText">Loading</span>
            <span className="loaderSubText">Content</span>
          </span>
        </Loader>
      </div>
    );

    let switchContent = (
      <Switch>
        <Route exact path={'/'} component={WorkflowManager} />
        <Route path={routes.PRODUCTS} component={Inventory} />
        <Route path={routes.STOREFRONTS} component={WorkflowManager} />
        <Route path={routes.DASHBOARD} component={Dashboard} />
        <Route exact path={'/configs'} component={ConfigurationManager} />
        <Route exact path={routes.WEBHOOKS} component={WebhooksPage} />
        <Route exact path={routes.SIGN_IN} component={() => <SignInPage />} />
        <Route exact path={routes.ACCOUNT} component={AccountPage} />
        <Route exact path={routes.NO_ACCESS} component={NoAccessPage} />
        <Route component={NotFoundPage} />
      </Switch>
    );

    if (this.props.app.authUser !== null) {
      //User is logged in
      if (
        !this.props.app.memberProfile ||
        !this.props.app.company ||
        !this.props.app.company.id
      ) {
        //Profile/Company has not been
        switchContent = loader;
      }
    }

    let flexDirection = 'row';
    if (this.props.app.authUser === null && !isMobileOnly) {
      flexDirection = 'column';
      content = switchContent;
    } else if (this.props.app.authUser === null && isMobileOnly) {
      flexDirection = 'column';
      content = switchContent;
    } else if (this.props.app.authUser !== null && isMobileOnly) {
      flexDirection = 'column';
      content = switchContent;
    } else {
      let webAppEmployee = true;
      let permissionId = 0;
      if (
        this.props.app.members !== undefined &&
        this.props.app.profile !== undefined
      ) {
        let memberId = this.props.app.profile.companies[
          this.props.app.profile.selectedCompany
        ].memberId;
        permissionId = this.props.app.members[memberId].permissionId;
      }
      if (permissionId < 2) {
        webAppEmployee = false;
      }

      content = (
        <FlexBox fill contained>
          {webAppEmployee ? (
            <Sidebar
              animation="push"
              icon="labeled"
              direction="left"
              onHide={() => {
                this.sidebarClosed();
              }}
              vertical="true"
              visible={true}
              width="thin"
            />
          ) : (
            undefined
          )}
          <Sidebar.Pusher
            style={{
              width: '100%',
              height: '100%',
              marginLeft: webAppEmployee ? '-150px' : '0px',
              paddingLeft: webAppEmployee ? '150px' : '0px',
            }}
          >
            <ErrorBoundary
              company={this.props.company}
              profile={this.props.app.profile}
            >
              <FlexBox row fill contained>
                {switchContent}
              </FlexBox>
            </ErrorBoundary>
          </Sidebar.Pusher>
        </FlexBox>
      );
    }

    let isLocalHost = window.location.hostname === 'localhost';
    let isDev = window.docketConfig === 'dev';

    let appNav;
    if (this.props.app.authUser) {
      appNav = (
        <AppNavigator
          key="app-nav"
          app={this.props.app}
          history={this.props.history}
          signOut={this.props.signOut}
        />
      );
    }

    return (
      <div className={styles.survcartApp}>
        <DragDropOverlay />
        <ErrorBoundary
          company={this.props.company}
          profile={this.props.app.profile}
        >
          <FlexBox row fill contained>
            {appNav}
            {switchContent}
          </FlexBox>
        </ErrorBoundary>
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  app: appSelectors.makeSelectApp(),
  company: appSelectors.selectCompany(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    signOut: params => dispatch(actions.signOut(params)),
    syncWorkflows: params => dispatch(actions.syncWorkflows(params)),
    syncConnectedAccounts: params =>
      dispatch(actions.syncConnectedAccounts(params)),
  };
}
const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const withReducer = injectReducer({ key: 'app', reducer });
const withSaga = injectSaga({ key: 'app', saga });

export default compose(
  withAuthentication,
  withReducer,
  withSaga,
  withRouter,
  withConnect,
)(App);
